import {
  Box,
  Card,
  Stack,
  Typography,
  IconButton,
  Divider,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Delete,
  AccessTime,
  Chat,
  SmartButton,
  WarningAmber,
  Description,
} from "@mui/icons-material";

const DripStep = ({ step, onDelete, readOnly = false }) => {
  const getButtonText = (buttonId, templateButtons) => {
    const button = templateButtons?.find(
      (button) => button.id.toString() === buttonId.toString()
    );
    return button?.text || button?.data?.buttonText || `Button ${buttonId}`;
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 3,
        position: "relative",
        boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
        "&:hover": { boxShadow: "0 6px 16px rgba(0,0,0,0.12)" },
        transition: "all 0.2s ease",
      }}
    >
      {/* Delete Button */}
      {readOnly === false && (
        <Tooltip title="Delete Step">
          <IconButton
            onClick={onDelete}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              bgcolor: "rgba(255,0,0,0.1)",
              "&:hover": { bgcolor: "rgba(255,0,0,0.2)" },
            }}
          >
            <Delete color="error" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Stack spacing={1.5}>
        {/* Header */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          {step?.stepName || "Untitled Step"}
        </Typography>

        <Divider />

        {/* Template Info */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Description color="primary" fontSize="small" />
          <Typography variant="body1">
            <strong>Template:</strong> {step?.template?.name || "—"}
          </Typography>
        </Stack>

        {/* Reply Delay */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccessTime color="primary" fontSize="small" />
          <Typography variant="body1">
            <strong>Reply Delay:</strong> {step?.delay ?? "—"} Hours
          </Typography>
        </Stack>

        {/* Wait for User Reply */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chat color="primary" fontSize="small" />
          <Typography variant="body1">
            <strong>Wait for User Reply:</strong>{" "}
            <Chip
              variant="outlined"
              label={step?.waitForUserReply ? "Yes" : "No"}
              color={step?.waitForUserReply ? "success" : "error"}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
        </Stack>

        {/* Button Reply Options */}
        {step?.waitForUserReply && step?.buttonReplyOptions && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", color: "text.secondary", mb: 0.5 }}
            >
              Button Reply Actions:
            </Typography>
            <Stack spacing={0.5} sx={{ pl: 2 }}>
              {Object.keys(step?.buttonReplyOptions)?.map((key) => (
                <Box
                  key={key}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <SmartButton color="primary" fontSize="small" />
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "capitalize" }}
                  >
                    <strong>
                      {getButtonText(key, step?.template?.buttons)}:
                    </strong>{" "}
                    {step?.buttonReplyOptions[key].label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* No Reply Option */}
        {step?.noReplyOption && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", color: "text.secondary", mb: 0.5 }}
            >
              On No Reply:
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ pl: 2 }}
            >
              <SmartButton color="primary" fontSize="small" />
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                {step?.noReplyOption?.label}
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>
    </Card>
  );
};

export default DripStep;
